/**
 * Noticing that a newer version of the app has been deployed.
 *
 * Leo, 2026-09-15, from an installed iOS app: *"when there is a new version there is no way
 * to refresh, you need to enable a 'refresh for new version' notification."*
 *
 * **An installed app is never closed, only backgrounded**, so it never navigates and never
 * re-fetches `index.html`. In a browser tab the problem does not exist: a reload picks up the
 * new release because CloudFront serves the HTML `max-age=0, must-revalidate` while the hashed
 * assets are immutable. Standalone there is no address bar, no reload button, and on iOS no
 * dependable pull-to-refresh — so a learner can run a build from last week indefinitely with
 * nothing to tell them and nothing to press.
 *
 * **The service worker cannot answer this.** It caches nothing on purpose (see `sw.js`), so
 * there is no cache version to compare, and `updatefound` fires only when the *worker file*
 * changes — which it almost never does, because the worker is static and the application is
 * what ships. A worker that started caching in order to detect its own staleness would be
 * introducing the bug it was detecting.
 *
 * So the check is the honest one: ask the server which bundle `index.html` points at now, and
 * compare it with the bundle this code is running from. No build step, no version file to
 * forget to bump, and nothing to keep in step — the hash Vite already puts in the filename is
 * the version, and the HTML is already authoritative about which one is current.
 */

/**
 * The bundle this code is running from, taken from its own module URL.
 *
 * `import.meta.url` rather than `document.currentScript`, which is null inside a module, and
 * rather than a constant injected at build time, which is a second source of truth that can
 * disagree with the filename.
 *
 * Returns the filename only. The origin is not interesting and including it would make a
 * comparison against a relative `src` in the HTML fail for reasons unrelated to the version.
 */
export function runningBundle(): string | undefined {
  try {
    return new URL(import.meta.url).pathname.split('/').pop() ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * The bundle name `index.html` currently points at, or `undefined` if that cannot be told.
 *
 * `cache: 'no-store'`, because the whole point is to bypass every cache between here and the
 * origin. Without it the browser can answer from its own HTTP cache and the check confirms the
 * version it is already running, for ever — a detector that always says "up to date" is worse
 * than none, because somebody will trust it.
 *
 * Every failure returns `undefined` rather than throwing or guessing. Offline, a captive
 * portal, a 503 mid-deploy: none of those means a new version exists, and an update banner
 * that appears because the network was briefly unavailable teaches people to ignore it.
 */
export async function deployedBundle(): Promise<string | undefined> {
  try {
    const response = await fetch('/index.html', { cache: 'no-store' });
    if (!response.ok) return undefined;
    const html = await response.text();
    // The module Vite emits, matched loosely enough to survive an attribute order change and
    // tightly enough not to match a preload hint for a lazily-loaded chunk.
    const found = /<script[^>]+src="\/?(assets\/index-[^"]+\.js)"/.exec(html);
    return found?.[1]?.split('/').pop();
  } catch {
    return undefined;
  }
}

/**
 * Whether a different version is deployed than the one running.
 *
 * Deliberately not "a newer one". There is no ordering in a content hash, so this reports
 * *difference*, and the only way to see a difference is a deploy — a rollback is equally worth
 * telling somebody about, and calling it "newer" in the copy would then be a lie.
 *
 * False whenever either side is unknown. The two ways to be wrong are not equal: a missed
 * update costs a learner one stale session, and a banner shown to somebody already running the
 * current version costs the banner its credibility everywhere.
 */
export async function updateAvailable(): Promise<boolean> {
  const running = runningBundle();
  if (!running) return false;
  const deployed = await deployedBundle();
  if (!deployed) return false;
  return deployed !== running;
}

/**
 * How often to look, when the app has been left open.
 *
 * Fifteen minutes is a compromise about an event nobody is waiting for. A deploy is not urgent
 * to a learner mid-answer, and the check costs one conditional request — but doing it every
 * minute would put a request on a phone's radio all day to notice something that happens
 * twice a week.
 */
export const UPDATE_POLL_MS = 15 * 60 * 1000;

/**
 * Watches for a new version, and calls back once when there is one.
 *
 * **Checks when the app is brought to the foreground**, which is the moment that matters for
 * an installed app: it is backgrounded for hours and looked at for seconds, and a deploy that
 * happened overnight should be noticed on the first glance rather than fifteen minutes into
 * it. `visibilitychange` is the only signal standalone iOS gives for that — there is no
 * navigation to hook.
 *
 * Fires the callback **at most once**. The banner does not need re-announcing, the check
 * keeps running after it has fired only to no purpose, and a caller that had to guard against
 * being told twice would be the third place this decision lived.
 *
 * Returns a teardown function. React calls it on unmount, and without it a remount would add
 * a second listener and a second timer to the same page.
 */
export function watchForUpdate(onFound: () => void): () => void {
  let stopped = false;

  const look = async (): Promise<void> => {
    if (stopped) return;
    if (await updateAvailable()) {
      if (stopped) return;
      stopped = true;
      onFound();
    }
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void look();
  };

  const timer = window.setInterval(() => void look(), UPDATE_POLL_MS);
  document.addEventListener('visibilitychange', onVisible);

  /*
   * Not on load. The page has just fetched `index.html` to exist at all, so it is running the
   * current version by construction, and a check in the first seconds can only produce a
   * banner from a race with a deploy happening at that exact moment.
   */

  return () => {
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
